// FILE: src/components/student/StreetAddressField.jsx
// The street address field of the address forms (getting started, My
// profile › Details). Always a normal text input with the browser's address
// autofill token, so Chrome/Safari/Firefox can fill the whole address. When
// Google address suggestions are configured (GET /api/public/info) it also
// becomes an accessible combobox: typing 3+ characters shows up to 5
// Canadian addresses from the Places API (New); choosing one fills the
// street, city, province and postal code through onAddressSelected.
// Typing is never blocked: suggestions are fetched in the background, and if
// Google fails the field simply stays (or goes back to) a plain input.
//
// The server keeps one street line (no separate unit field), so the token is
// "street-address" (the whole street part, unit included) rather than
// "address-line1", which would make autofill drop the apartment or unit.
import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Autocomplete, Box, Paper, TextField, Typography } from "@mui/material";
import { useForkRef } from "@mui/material/utils";
import useAddressSuggestions from "../../hooks/useAddressSuggestions";
import {
  addressForSuggestion,
  fetchAddressSuggestions,
  markSuggestionsUnavailable
} from "../../services/addressSuggestions";
import { visuallyHidden } from "../ui";

export const STREET_AUTOCOMPLETE = "street-address";
const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;
const FIELD_NAMES = { street: "street", city: "city", province: "province", postalCode: "postal code" };

/** Google's attribution, shown under the suggestions whenever they are (Google Maps Platform terms). */
export const SuggestionsPaper = forwardRef(function SuggestionsPaper({ children, ...props }, ref) {
  return (
    <Paper ref={ref} {...props}>
      {children}
      <Box
        // Clicking the attribution must not take focus from the field.
        onMouseDown={(e) => e.preventDefault()}
        sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Typography
          data-testid="google-attribution"
          sx={{
            fontSize: "1rem",
            fontWeight: 400,
            letterSpacing: "normal",
            color: (theme) => theme.vars.palette.zun.googleAttribution
          }}
        >
          Address suggestions by <span translate="no">Google Maps</span>
        </Typography>
      </Box>
    </Paper>
  );
});

function filledText(address) {
  const names = Object.keys(FIELD_NAMES).filter((f) => address[f]).map((f) => FIELD_NAMES[f]);
  if (!names.length) return "";
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  return `Address filled in: ${list}. Check them before you save.`;
}

function SuggestingInput({ places, id, name, value, onChange, onAddressSelected, inputRef, fieldProps, maxLength, setAnnouncement }) {
  const [options, setOptions] = useState([]);
  const timer = useRef(null);
  const requestSeq = useRef(0);
  const editSeq = useRef(0);
  const token = useRef(null);
  const failures = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const sessionToken = () => {
    if (!token.current) token.current = new places.AutocompleteSessionToken();
    return token.current;
  };

  function search(text) {
    clearTimeout(timer.current);
    const seq = ++requestSeq.current;
    const query = text.trim();
    if (query.length < MIN_CHARS) {
      setOptions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const found = await fetchAddressSuggestions(places, query, sessionToken());
        if (seq !== requestSeq.current) return; // a newer keystroke won
        failures.current = 0;
        setOptions(found);
        setAnnouncement(
          found.length
            ? `${found.length} address ${found.length === 1 ? "suggestion" : "suggestions"} from Google Maps. Use the up and down arrow keys to choose one, or keep typing.`
            : ""
        );
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setOptions([]);
        failures.current += 1;
        // Twice in a row (bad key, API not enabled, offline): plain field for the rest of the visit.
        if (failures.current >= 2) markSuggestionsUnavailable();
        if (process.env.NODE_ENV !== "test") console.warn("Address suggestions failed:", err?.message || err);
      }
    }, DEBOUNCE_MS);
  }

  async function choose(option) {
    clearTimeout(timer.current);
    requestSeq.current += 1; // ignore suggestions still on their way
    setOptions([]);
    const at = editSeq.current;
    onChange(option.main);
    try {
      const address = await addressForSuggestion(option);
      if (editSeq.current !== at) return; // the student kept typing: keep what they typed
      onAddressSelected(address);
      setAnnouncement(filledText(address));
    } catch (err) {
      if (process.env.NODE_ENV !== "test") console.warn("Address details failed:", err?.message || err);
    } finally {
      token.current = null; // a new billing session for the next search
    }
  }

  return (
    <Autocomplete
      id={id}
      freeSolo
      disableClearable
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={null}
      inputValue={value}
      onInputChange={(event, text, reason) => {
        if (reason !== "input") return;
        editSeq.current += 1;
        onChange(text);
        search(text);
      }}
      onChange={(event, option) => {
        if (option && typeof option === "object") choose(option);
      }}
      slots={{ paper: SuggestionsPaper }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box>
              <Typography component="span" sx={{ display: "block", fontWeight: 600 }}>
                {option.main}
              </Typography>
              {option.secondary && (
                <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
                  {option.secondary}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...fieldProps}
          inputRef={inputRef}
          inputProps={{ ...params.inputProps, name, autoComplete: STREET_AUTOCOMPLETE, maxLength }}
        />
      )}
    />
  );
}

/**
 * Props: id, name ("street"), label, value, onChange(text),
 * onAddressSelected({ street?, city?, province?, postalCode? }), inputRef,
 * required, error, helperText, maxLength (120), sx.
 */
export default function StreetAddressField({
  id,
  name = "street",
  label = "Street address",
  value,
  onChange,
  onAddressSelected,
  inputRef,
  required = false,
  error = false,
  helperText,
  maxLength = 120,
  sx
}) {
  const places = useAddressSuggestions();
  const [announcement, setAnnouncement] = useState("");
  const ownRef = useRef(null);
  const ref = useForkRef(ownRef, inputRef);

  // Suggestions arrive (or go away) after the field is on screen: if the
  // student is already typing, the new input keeps the focus and the caret.
  const mode = places ? "suggest" : "plain";
  const lastMode = useRef(mode);
  const restoreFocus = useRef(false);
  if (lastMode.current !== mode) {
    restoreFocus.current = !!ownRef.current && typeof document !== "undefined" && document.activeElement === ownRef.current;
    lastMode.current = mode;
  }
  useLayoutEffect(() => {
    const el = ownRef.current;
    if (!restoreFocus.current || !el) return;
    restoreFocus.current = false;
    el.focus();
    const end = el.value.length;
    try {
      el.setSelectionRange(end, end);
    } catch {
      /* not a text input */
    }
  }, [mode]);

  const fieldProps = { label, required, error, helperText, fullWidth: true };

  return (
    <Box sx={[{ position: "relative", minWidth: 0 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      {places ? (
        <SuggestingInput
          places={places}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onAddressSelected={onAddressSelected}
          inputRef={ref}
          fieldProps={fieldProps}
          maxLength={maxLength}
          setAnnouncement={setAnnouncement}
        />
      ) : (
        <TextField
          {...fieldProps}
          id={id}
          name={name}
          inputRef={ref}
          autoComplete={STREET_AUTOCOMPLETE}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { maxLength } }}
        />
      )}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {announcement}
      </Box>
    </Box>
  );
}
